<template>
  <Head :title="`${props.order.name}`" />

  <div class="mx-auto w-11/12 max-w-[1200px] space-y-6 py-6">
    <!-- Header Card -->
    <OrderHeaderCard
      :order="props.order"
      :has-documents="documentsList.length > 0"
      @connect-table="showConnectTableModal = true"
      @cancel-closing="cancelClosing"
      @export-all="exportAll"
      @edit="showEditDrawer = true"
      @delete="deleteOrder"
      @download-originals="downloadFile('originals')"
      @download-signed="downloadFile('signed')"
      @download-closing-report="downloadClosingReport"
      @push-to-agent="pushToAgent"
      @cancel-push="cancelPush"
    />

    <!-- Two-column layout: Documents (main) + Sidebar -->
    <div class="grid grid-cols-[1fr_360px] items-start gap-6">
      <!-- Main: Documents -->
      <AppCard no-padding>
        <DocumentsList
          :documents="documentsList"
          :order="props.order"
          @pull-documents="showPullDocumentsDrawer = true"
        />
      </AppCard>

      <!-- Sidebar: Participants + Activity -->
      <div class="flex flex-col gap-6">
        <AppCard no-padding>
          <ParticipantsList
            :order="props.order"
            :participants="participantsList"
            @edit-imported="showEditParticipantsDrawer = true"
          />
        </AppCard>

        <AppCard no-padding>
          <ActivityTimeline
            :events="orderEventsList"
            :order-id="props.order.gid"
          />
        </AppCard>
      </div>
    </div>

    <ConnectTableModal
      v-model:visible="showConnectTableModal"
      :order-id="props.order.gid"
      @success="handleConnectTableSuccess"
    />

    <OrderEditDrawer
      v-model:visible="showEditDrawer"
      :order="props.order"
      @success="router.reload()"
    />

    <EditParticipantsDrawer
      v-model:visible="showEditParticipantsDrawer"
      :order="props.order"
      @success="router.reload()"
    />

    <PullDocumentsDrawer
      v-model:visible="showPullDocumentsDrawer"
      :order="props.order"
      @success="router.reload()"
    />
  </div>
</template>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3"
import { useConfirm } from "primevue/useconfirm"
import { useToast } from "primevue/usetoast"
import { computed, ref } from "vue"

import ConnectTableModal from "../../components/ConnectTableModal.vue"
import ActivityTimeline from "../../components/order/ActivityTimeline.vue"
import DocumentsList from "../../components/order/DocumentsList.vue"
import EditParticipantsDrawer from "../../components/order/EditParticipantsDrawer.vue"
import OrderEditDrawer from "../../components/order/OrderEditDrawer.vue"
import OrderHeaderCard from "../../components/order/OrderHeaderCard.vue"
import ParticipantsList from "../../components/order/ParticipantsList.vue"
import PullDocumentsDrawer from "../../components/order/PullDocumentsDrawer.vue"
import AppCard from "../../components/ui/AppCard.vue"
import { useOrderMutations } from "../../composables/useOrderMutations"
import { useOrderSubscription } from "../../composables/useOrderSubscription"
import type { OrderType } from "./types"

const toast = useToast()
const confirm = useConfirm()
const {
  cancelOrder: cancelOrderMutation,
  destroyOrder: destroyOrderMutation,
  pushDocuments: pushDocumentsMutation,
  cancelPush: cancelPushMutation,
} = useOrderMutations(toast)

// ─── State ───────────────────────────────────────────────────────

const showConnectTableModal = ref(false)
const showEditDrawer = ref(false)
const showEditParticipantsDrawer = ref(false)
const showPullDocumentsDrawer = ref(false)

// ─── Props ───────────────────────────────────────────────────────

const props = defineProps<{
  order: OrderType
}>()

useOrderSubscription(props.order.gid)

// ─── Computed ────────────────────────────────────────────────────

const documentsList = computed(() =>
  (props.order.documents.nodes ?? []).filter((n) => n != null),
)
const participantsList = computed(() =>
  (props.order.participants.nodes ?? []).filter((n) => n != null),
)
const orderEventsList = computed(() => props.order.orderEvents)

// ─── Header Card Actions ─────────────────────────────────────────

const handleConnectTableSuccess = () => {
  toast.add({
    severity: "success",
    summary: "Success",
    detail: "Connected to closing table.",
    life: 4000,
  })
  router.reload()
}

const cancelClosing = () => {
  confirm.require({
    message:
      "This will disconnect the closing table and return the order to draft. All signatures will be removed. Are you sure?",
    header: "Cancel Closing",
    icon: "pi pi-exclamation-triangle",
    acceptClass: "p-button-danger",
    accept: () => {
      void performCancelClosing()
    },
  })
}

const performCancelClosing = async () => {
  const result = await cancelOrderMutation(props.order.gid)
  if (result.success) {
    router.reload()
  }
}

const performDeleteOrder = async () => {
  const result = await destroyOrderMutation(props.order.gid)
  if (result.success) {
    router.visit(`/companies/${props.order.company.id}/orders`)
  }
}

const deleteOrder = () => {
  confirm.require({
    message:
      "Are you sure you want to delete this order? This action cannot be undone.",
    header: "Delete Order",
    icon: "pi pi-trash",
    acceptClass: "p-button-danger",
    accept: () => {
      void performDeleteOrder()
    },
  })
}

const pushToAgent = async () => {
  const result = await pushDocumentsMutation(props.order.gid)
  if (result.success) {
    router.reload()
  }
}

const cancelPush = async () => {
  const result = await cancelPushMutation(props.order.gid)
  if (result.success) {
    router.reload()
  }
}

const downloadFile = (type: "originals" | "signed") => {
  window.open(`/orders/${props.order.gid}/download/${type}`, "_blank")
}

const exportAll = () => {
  window.open(`/orders/${props.order.gid}/download/signed`, "_blank")
}

const downloadClosingReport = () => {
  window.open(
    `/orders/${props.order.gid}/closing_report?t=${Date.now()}`,
    "_blank",
  )
}
</script>
